import { useMemo } from 'react';

import type { Artist, AccessLevel } from '../interfaces/maestra';
import { PAYWALL_DISABLED } from '../constants/maestra';
import {
  MODULOS_DO_PERFIL, PAGINAS_DO_ACESSO, acessosDoMembro, acessosPorPagina, type AcessoAoModulo, type ModuloDoPerfil, type PaginaDoAcesso,
} from '../nucleo/permissoesDoMembro';
import { useEntitlements, FREE_MAX_CATALOG_TRACKS } from './useEntitlements';
import { perfilEstaAtivo } from '../nucleo/perfilAtivo';

// Capacidades por (usuário, artista), combinando:
//   - eixo CONTA: isPro (assinatura Maestra PRO ativa ou em carência) — da conta LOGADA;
//   - eixo PERFIL: isPaid — o perfil está ATIVO, por um de dois caminhos:
//       legado  → comprou o pagamento único (ou ganhou um Pass Access): vitalício, fora da cota;
//       assinado → a conta DONA é PRO e o perfil cabe na cota de perfis.
//   - papel: isOwner (dono) vs colaborador (role === 'member').
//
// ⚠️ O ACESSO DO CONVIDADO É POR MÓDULO, e quem liga a caneta é a assinatura de QUEM CONVIDOU.
//
// Desde 03/10/2026 o titular escolhe, em cada módulo, Sem acesso, Ver ou Editar
// (`nucleo/permissoesDoMembro`); os membros de antes têm `full`, Editar em tudo. Editar continua
// exigindo o perfil do titular ativo: sem ele, todo módulo fica em Ver. O histórico abaixo é o do
// privilégio único, que vale ainda para a regra da assinatura.
//
// Até 2026-09-23 cada convite concedia NÍVEIS ('catalog', 'agenda', 'plan', 'team'), e o
// colaborador editava módulo a módulo conforme o que tinha sido marcado. Isso acabou: todo
// convidado edita o CONTEÚDO do perfil — músicas, agenda, planejamento — enquanto o perfil do
// titular estiver ativo, e nada quando não estiver. Todo o custo fica com quem criou o perfil.
//
// A regra caiu em `isPaid` sem precisar de dado novo, e isso não é coincidência: `perfilEstaAtivo`
// já lia `owner_is_pro` para o membro. Os dois cenários do produto são, literalmente,
// `isPaid === true` e `isPaid === false` para quem é colaborador.
//
// ADMINISTRAR NÃO É EDITAR. Convidar, remover pessoas e excluir o perfil continuam só do dono —
// é a única capacidade que PERDE alcance nesta mudança (o nível 'team' as concedia).
//
// IMPORTANTE: o gating de front NÃO é segurança. Quem protege é a RLS, e a partir desta mudança
// ela também consulta a assinatura do dono (`perfil_com_assinatura_ativa`).

export interface ArtistCapabilities {
  isPaid: boolean;
  isOwner: boolean;
  /**
   * Convidado de um perfil cujo titular está sem assinatura: entra, lê tudo, não grava nada.
   *
   * É o gatilho do aviso — a superfície abre o modal quando isto é true e alguém tenta salvar.
   * Não se confunde com `!isPaid`: o dono sem assinatura vê a tela de venda, que para o
   * convidado seria cobrança indevida, porque assinar não destravaria o perfil alheio.
   */
  somenteLeituraPeloTitular: boolean;
  /** O que o titular liberou ao convidado, módulo a módulo. O dono tem Editar em tudo. */
  acesso: Record<ModuloDoPerfil, AcessoAoModulo>;
  /**
   * O convidado vê o módulo, mas não grava: o titular deu só Ver, ou está sem assinatura. É o que
   * as telas leem para desligar a caneta e abrir o aviso (que diz qual dos dois motivos).
   */
  somenteLeituraNo: Record<ModuloDoPerfil, boolean>;
  /** O mesmo, página por página (03/11/2026): Perfil, Playlists, Branding e Material têm o seu. */
  acessoNaPagina: Record<PaginaDoAcesso, AcessoAoModulo>;
  somenteLeituraNaPagina: Record<PaginaDoAcesso, boolean>;
  /** Dono ou convidado de perfil ativo: pode colaborar no Espaço JAM. */
  canCollaborateJam: boolean;
  canEditCatalog: boolean;
  canEditAgenda: boolean;
  canManageTeam: boolean;
  viewPlanning: boolean;
  editPlanning: boolean;
  manageTasks: boolean;
  useNytaMaestra: boolean;
  useNytaConsultora: boolean;
  maxCatalogTracks: number;
}

export function deriveArtistCapabilities(args: {
  isPro: boolean; // PRO da conta do usuário atual
  isPaid: boolean; // perfil ATIVO (legado vitalício ou coberto pela assinatura do dono)
  isOwner: boolean;
  ownerIsPro?: boolean; // PRO do DONO do perfil (só quando !isOwner)
  /** O perfil foi liberado por código de acesso: o planejamento e o plano abrem, os módulos não. */
  liberadoPorCodigo?: boolean;
  /** Os níveis do convite (`artist_members.access_levels`). Só valem para o convidado. */
  accessLevels?: readonly (AccessLevel | string)[] | null;
}): ArtistCapabilities {
  const { isPro, isPaid, isOwner, ownerIsPro, liberadoPorCodigo } = args;
  const acesso = isOwner
    ? Object.fromEntries(MODULOS_DO_PERFIL.map((modulo) => [modulo, 'editar'])) as Record<ModuloDoPerfil, AcessoAoModulo>
    : acessosDoMembro(args.accessLevels);
  const somenteLeituraNo = Object.fromEntries(MODULOS_DO_PERFIL.map((modulo) => (
    [modulo, !isOwner && (acesso[modulo] !== 'editar' || !isPaid)]
  ))) as Record<ModuloDoPerfil, boolean>;
  const acessoNaPagina = isOwner
    ? Object.fromEntries(PAGINAS_DO_ACESSO.map((p) => [p, 'editar'])) as Record<PaginaDoAcesso, AcessoAoModulo>
    : acessosPorPagina(args.accessLevels);
  const somenteLeituraNaPagina = Object.fromEntries(PAGINAS_DO_ACESSO.map((p) => (
    [p, !isOwner && (acessoNaPagina[p] !== 'editar' || !isPaid)]
  ))) as Record<PaginaDoAcesso, boolean>;
  const edita = (m: ModuloDoPerfil) => acesso[m] === 'editar';
  // O CÓDIGO DE ACESSO LIBERA SÓ O PLANEJAMENTO (decisão do Lucas, 27/09/2026): o dono de um perfil
  // liberado por código vê e faz o planejamento e acompanha o plano, sem que o perfil fique ativo
  // (os módulos, a gestão de tarefas e a Nyta seguem pedindo o PRO).
  const planejamentoPeloCodigo = isOwner && !!liberadoPorCodigo;

  // QUEM PAGA ESTE PERFIL. Para o dono é a conta dele; para o convidado é sempre a do titular,
  // nunca a dele. Era `profilePro` e só o limite de faixas usava; agora rege também as edições
  // avançadas do plano.
  const proDoPagador = PAYWALL_DISABLED ? true : isOwner ? isPro : !!ownerIsPro;

  // A CANETA DO PERFIL: perfil ATIVO, para o dono e para o convidado. Cada módulo ainda pede o
  // Editar dele. Era `isOwner || isPaid`: o dono de um perfil inativo (grátis, cancelado, Pass
  // Access, fora da cota) tinha caneta, e o app (sem o portão das rotas da web) oferecia criar e
  // apagar músicas e eventos que o banco recusa desde que a trava de escrita foi ligada (QA de
  // pagamentos, A5): a inclusão dava erro e a exclusão "funcionava" e voltava na recarga.
  const podeEditarConteudo = isPaid;

  // LER É SEMPRE, para o convidado. É o segundo cenário do produto: ele não é expulso do perfil,
  // ele fica sem caneta. Para o dono, `isPaid` como antes — quem não assina vê a tela de venda.
  const podeVer = isOwner ? isPaid : true;

  return {
    isPaid,
    isOwner,
    somenteLeituraPeloTitular: !isOwner && !isPaid,
    acesso,
    somenteLeituraNo,
    acessoNaPagina,
    somenteLeituraNaPagina,
    canCollaborateJam: podeEditarConteudo && edita('catalog'),
    canEditCatalog: podeEditarConteudo && edita('catalog'),
    canEditAgenda: podeEditarConteudo && edita('agenda'),
    canManageTeam: isOwner,
    viewPlanning: (podeVer && acesso.plan !== 'nenhum') || planejamentoPeloCodigo,
    // Escrito com as duas condições, embora hoje coincidam: são independentes, e a expressão
    // documenta que manter tarefas exige perfil ativo E direito de editar.
    editPlanning: (isPaid && podeEditarConteudo && edita('plan')) || planejamentoPeloCodigo,
    // ⚠️ SEGUE O PAGADOR, e não o PRO de quem clica. Há perfis legado em que `isPaid` é true sem
    // assinatura nenhuma: neles o DONO não tem `manageTasks`, e concedê-lo ao convidado daria ao
    // convidado mais poder que ao dono. Com `proDoPagador` os dois caem juntos.
    manageTasks: isPaid && proDoPagador && podeEditarConteudo && edita('plan'),
    useNytaMaestra: isPaid && podeEditarConteudo,
    // ⚠️ A NYTA SEGUE QUEM PAGA O PERFIL (decisão do Lucas, 24/09/2026). O dono usa pela assinatura
    // dele; o convidado, pela do titular, com o perfil ativo, e cada um com o próprio limite diário
    // (a edge conta por `user_id` + `artist_id`). Era o PRO de quem conversa, e o convidado de um
    // titular pagante via a Nyta trancada. O gate soberano continua na edge `nyta-chat`, que
    // aplica esta MESMA regra: front e servidor mudaram juntos.
    // O perfil ativo também vale para o dono: num perfil fora da cota a Nyta fica trancada.
    useNytaConsultora: PAYWALL_DISABLED || (isPaid && proDoPagador),
    maxCatalogTracks: proDoPagador ? Infinity : FREE_MAX_CATALOG_TRACKS,
  };
}

// A regra do perfil ATIVO mora no núcleo (`nucleo/perfilAtivo`), pura: quem só precisa dela não
// carrega este hook (e os testes que o trocam não a levam junto). Daqui, ela segue exportada.
export { perfilEstaAtivo } from '../nucleo/perfilAtivo';

export function useArtistCapabilities(artist?: Artist | null): ArtistCapabilities {
  const { isPro } = useEntitlements();

  return useMemo(() => {
    const isOwner = !artist || artist.role !== 'member';
    const isPaid = perfilEstaAtivo(artist, isPro);
    return deriveArtistCapabilities({
      isPro,
      isPaid,
      isOwner,
      ownerIsPro: artist?.owner_is_pro,
      liberadoPorCodigo: artist?.liberado_por_codigo,
      accessLevels: artist?.access_levels,
    });
  }, [artist, isPro]);
}

export default useArtistCapabilities;
